import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { PageLoader } from '../components/shared/LoadingSpinner';
import { StatusBadge } from '../components/shared/StatusBadge';
import { formatDateTime, formatDate } from '../lib/utils';
import { 
  FileSearch, 
  Shield, 
  FileCheck, 
  AlertTriangle, 
  Clock, 
  Download,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend 
} from 'recharts';
import { toast } from 'sonner';
import api from '../lib/api';

const COLORS = {
  compliant: '#10b981',
  pending: '#64748b',
  gap: '#ef4444',
};

export default function AuditPortalPage() {
  const [summary, setSummary] = useState(null);
  const [evidenceReport, setEvidenceReport] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    frameworks: true,
    evidence: true,
    auditTrail: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, evidenceRes, logsRes] = await Promise.all([
        api.get('/audit-portal/summary'),
        api.get('/audit-portal/evidence-report'),
        api.get('/audit-logs?limit=50'),
      ]);
      setSummary(summaryRes.data);
      setEvidenceReport(evidenceRes.data);
      setAuditLogs(logsRes.data);
    } catch (error) {
      toast.error('Failed to load audit data');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const exportReport = () => {
    const reportData = {
      generated_at: new Date().toISOString(),
      summary: summary,
      evidence_report: evidenceReport,
      audit_logs: auditLogs
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `grc-audit-report-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  const downloadPdfReport = async () => {
    setDownloadingPdf(true);
    try {
      const response = await api.get('/reports/compliance-summary', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compliance-report-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate PDF report');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <PageLoader />
      </DashboardLayout>
    );
  }

  const stats = summary?.overall_stats;
  
  const complianceData = stats ? [
    { name: 'Compliant', value: stats.compliant_controls, color: COLORS.compliant },
    { name: 'Pending', value: stats.pending_controls, color: COLORS.pending },
    { name: 'Gap', value: stats.gap_controls, color: COLORS.gap },
  ].filter(d => d.value > 0) : [];

  const evidenceByStatus = evidenceReport?.summary?.by_status 
    ? Object.entries(evidenceReport.summary.by_status).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value,
        fill: COLORS[name] || '#64748b'
      }))
    : [];

  return (
    <DashboardLayout>
      <PageHeader 
        title="Audit Portal" 
        description="Comprehensive compliance overview for auditors"
        actions={
          <div className="flex gap-3">
            <Button
              data-testid="download-pdf-report-btn"
              onClick={downloadPdfReport}
              disabled={downloadingPdf}
              className="bg-blue-600 hover:bg-blue-700 rounded-sm"
            >
              <Download size={18} className="mr-2" />
              {downloadingPdf ? 'Generating PDF...' : 'Download PDF Report'}
            </Button>
            <Button
              data-testid="export-report-btn"
              variant="outline"
              onClick={exportReport}
              className="rounded-sm"
            >
              <Download size={18} className="mr-2" />
              Export JSON
            </Button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-8 md:px-8 lg:px-12">
        {/* Generated Timestamp */}
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-6">
          <div className="flex items-center gap-2 text-blue-700">
            <Clock size={18} />
            <span className="text-sm">
              Report generated: {formatDateTime(summary?.generated_at)}
            </span>
          </div>
        </div>

        {/* Overview Section */}
        <Collapsible open={expandedSections.overview} onOpenChange={() => toggleSection('overview')}>
          <div className="bg-white border border-slate-200 rounded-sm mb-6">
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100">
                <div className="flex items-center gap-2">
                  {expandedSections.overview ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  <h3 className="font-secondary font-semibold text-lg text-slate-900">
                    Compliance Overview
                  </h3>
                </div>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <StatCard
                    icon={Shield}
                    label="Total Controls"
                    value={stats?.total_controls || 0}
                    subValue={`${stats?.compliance_percentage || 0}% compliant`}
                    iconColor="text-blue-600"
                    bgColor="bg-blue-50"
                  />
                  <StatCard
                    icon={FileCheck}
                    label="Evidence Items"
                    value={stats?.total_evidence || 0}
                    subValue="collected"
                    iconColor="text-emerald-600"
                    bgColor="bg-emerald-50"
                  />
                  <StatCard
                    icon={AlertTriangle}
                    label="Active Risks"
                    value={stats?.total_risks || 0}
                    subValue={`${stats?.high_risks || 0} high priority`}
                    iconColor="text-amber-600"
                    bgColor="bg-amber-50"
                  />
                  <StatCard
                    icon={FileSearch}
                    label="Frameworks"
                    value={stats?.total_frameworks || 0}
                    subValue="loaded"
                    iconColor="text-violet-600"
                    bgColor="bg-violet-50"
                  />
                </div>

                {/* Compliance Chart */}
                {complianceData.length > 0 && (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={complianceData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {complianceData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* Framework Summary Section */}
        <Collapsible open={expandedSections.frameworks} onOpenChange={() => toggleSection('frameworks')}>
          <div className="bg-white border border-slate-200 rounded-sm mb-6">
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100">
                <div className="flex items-center gap-2">
                  {expandedSections.frameworks ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  <h3 className="font-secondary font-semibold text-lg text-slate-900">
                    Framework Compliance Status
                  </h3>
                </div>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="p-6">
                {summary?.frameworks?.length > 0 ? (
                  <div className="space-y-4">
                    {summary.frameworks.map((fw, index) => (
                      <div key={index} className="border border-slate-200 rounded-sm p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-semibold text-slate-900">{fw.name}</h4>
                            <span className="text-xs text-slate-500">Version {fw.version}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-slate-900">{fw.compliance_percentage}%</p>
                            <p className="text-xs text-slate-500">compliant</p>
                          </div>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className="bg-emerald-500 h-2 rounded-full transition-all"
                            style={{ width: `${fw.compliance_percentage}%` }}
                          />
                        </div>
                        <div className="flex justify-between mt-2 text-xs text-slate-500">
                          <span>{fw.compliant_controls} of {fw.total_controls} controls compliant</span>
                          <span>{fw.total_controls - fw.compliant_controls} remaining</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No frameworks loaded</p>
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* Evidence Summary Section */}
        <Collapsible open={expandedSections.evidence} onOpenChange={() => toggleSection('evidence')}>
          <div className="bg-white border border-slate-200 rounded-sm mb-6">
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100">
                <div className="flex items-center gap-2">
                  {expandedSections.evidence ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  <h3 className="font-secondary font-semibold text-lg text-slate-900">
                    Evidence Summary ({evidenceReport?.summary?.total || 0} items)
                  </h3>
                </div>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* By Status */}
                  <div>
                    <h4 className="font-medium text-slate-700 mb-3">By Status</h4>
                    {evidenceByStatus.length > 0 ? (
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={evidenceByStatus}>
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-slate-400 text-center py-8">No evidence data</p>
                    )}
                  </div>

                  {/* By Source */}
                  <div>
                    <h4 className="font-medium text-slate-700 mb-3">By Source</h4>
                    {evidenceReport?.summary?.by_source && Object.keys(evidenceReport.summary.by_source).length > 0 ? (
                      <div className="space-y-2">
                        {Object.entries(evidenceReport.summary.by_source).map(([source, count]) => (
                          <div key={source} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <span className="capitalize text-slate-700">{source}</span>
                            <span className="font-mono font-medium">{count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-center py-8">No evidence data</p>
                    )}
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* Audit Trail Section */}
        <Collapsible open={expandedSections.auditTrail} onOpenChange={() => toggleSection('auditTrail')}>
          <div className="bg-white border border-slate-200 rounded-sm">
            <CollapsibleTrigger asChild>
              <button className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100">
                <div className="flex items-center gap-2">
                  {expandedSections.auditTrail ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  <h3 className="font-secondary font-semibold text-lg text-slate-900">
                    Audit Trail (Recent Activity)
                  </h3>
                </div>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="p-6">
                {auditLogs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Timestamp</th>
                          <th>User</th>
                          <th>Action</th>
                          <th>Entity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLogs.slice(0, 20).map((log) => (
                          <tr key={log.id} className="table-row-hover">
                            <td className="text-sm text-slate-500 font-mono">
                              {formatDateTime(log.timestamp)}
                            </td>
                            <td className="text-sm">{log.user_email}</td>
                            <td>
                              <span className="badge badge-neutral uppercase text-xs">
                                {log.action}
                              </span>
                            </td>
                            <td className="text-sm text-slate-600">
                              {log.entity_type}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-slate-400 text-center py-8">No audit logs available</p>
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      </div>
    </DashboardLayout>
  );
}

const StatCard = ({ icon: Icon, label, value, subValue, iconColor, bgColor }) => (
  <div className="stat-card">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-slate-500 uppercase tracking-wider font-mono">
          {label}
        </p>
        <p className="mt-2 text-3xl font-secondary font-bold text-slate-900">
          {value}
        </p>
        <p className="mt-1 text-sm text-slate-500">{subValue}</p>
      </div>
      <div className={`p-3 rounded-sm ${bgColor}`}>
        <Icon size={24} className={iconColor} />
      </div>
    </div>
  </div>
);
